// Tabla horaria (columnas a-p) del partograma (051, Fase 4 Tanda 2a) —
// constantes materno-fetales por control, debajo del gráfico de dilatación.
// Solo texto libre por celda (16 columnas × 8 campos ya es suficiente
// densidad de UI sin agregar un Select por celda de posición materna).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COLUMNAS_HORARIAS_PARTOGRAMA,
  INTENSIDADES_DOLOR_PARTOGRAMA,
  LOCALIZACIONES_DOLOR_PARTOGRAMA,
  POSICIONES_MATERNAS_PARTOGRAMA,
  type ColumnaHorariaPartograma,
  type FilaHorariaPartogramaPdf,
} from '@logiacare/hce-pdf';
import { Input } from '@/components/ui/input';

export interface FilaHorariaEstado051 {
  readonly horaReal: string;
  readonly posicionMaterna: string;
  readonly tensionArterial: string;
  readonly pulsoMaterno: string;
  readonly frecuenciaCardiacaFetal: string;
  readonly duracionContracciones: string;
  readonly frecuenciaContracciones: string;
  readonly dolorLocalizacion: string;
  readonly dolorIntensidad: string;
}

export type EstadoTablaHorariaPartograma051 = Readonly<Record<ColumnaHorariaPartograma, FilaHorariaEstado051>>;

const FILA_VACIA: FilaHorariaEstado051 = {
  horaReal: '',
  posicionMaterna: '',
  tensionArterial: '',
  pulsoMaterno: '',
  frecuenciaCardiacaFetal: '',
  duracionContracciones: '',
  frecuenciaContracciones: '',
  dolorLocalizacion: '',
  dolorIntensidad: '',
};

export function estadoInicialTablaHorariaPartograma051(): EstadoTablaHorariaPartograma051 {
  return Object.fromEntries(COLUMNAS_HORARIAS_PARTOGRAMA.map((c) => [c, { ...FILA_VACIA }])) as EstadoTablaHorariaPartograma051;
}

/** Solo columnas con al menos un campo cargado, para `DatosPartogramaPdf.filasHorarias` (lista dispersa, mismo criterio que `DatosOdontogramaPdf`). */
export function filasHorariasConDato(estado: EstadoTablaHorariaPartograma051): FilaHorariaPartogramaPdf[] {
  return COLUMNAS_HORARIAS_PARTOGRAMA.map((columna) => ({ columna, ...estado[columna] })).filter((fila) =>
    Object.values(estado[fila.columna]).some((v) => v.trim() !== ''),
  );
}

const HORA_REAL = { clave: 'horaReal', i18n: 'horaReal' } as const;
const CAMPOS_TEXTO: readonly { clave: keyof FilaHorariaEstado051; i18n: string }[] = [
  { clave: 'tensionArterial', i18n: 'tensionArterial' },
  { clave: 'pulsoMaterno', i18n: 'pulsoMaterno' },
  { clave: 'frecuenciaCardiacaFetal', i18n: 'frecuenciaCardiacaFetal' },
  { clave: 'duracionContracciones', i18n: 'duracionContracciones' },
  { clave: 'frecuenciaContracciones', i18n: 'frecuenciaContracciones' },
];

const CLASE_SELECT = 'h-7 w-20 rounded-md border border-input bg-transparent px-1 text-xs';

interface FilaSelectProps {
  readonly etiqueta: string;
  readonly opciones: readonly string[];
  readonly textoOpcion: (opcion: string) => string;
  readonly valor: (columna: ColumnaHorariaPartograma) => string;
  readonly onChange: (columna: ColumnaHorariaPartograma, valor: string) => void;
}

/** Fila de la tabla horaria con un <select> nativo por columna (a-p): las opciones son los códigos impresos en las referencias del papel. */
function FilaSelect({ etiqueta, opciones, textoOpcion, valor, onChange }: FilaSelectProps): JSX.Element {
  return (
    <tr className="border-t border-border">
      <td className="sticky left-0 z-10 bg-hce-surface whitespace-nowrap p-1 pr-2 font-medium text-hce-ink">{etiqueta}</td>
      {COLUMNAS_HORARIAS_PARTOGRAMA.map((c) => (
        <td key={c} className="p-0.5">
          <select className={CLASE_SELECT} aria-label={`${etiqueta} ${c}`} value={valor(c)} onChange={(e) => onChange(c, e.target.value)}>
            <option value="">—</option>
            {opciones.map((o) => (
              <option key={o} value={o}>
                {textoOpcion(o)}
              </option>
            ))}
          </select>
        </td>
      ))}
    </tr>
  );
}

export interface TablaHorariaProps {
  readonly estado: EstadoTablaHorariaPartograma051;
  readonly onChange: (estado: EstadoTablaHorariaPartograma051) => void;
}

export function TablaHoraria({ estado, onChange }: TablaHorariaProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarCelda(columna: ColumnaHorariaPartograma, campo: keyof FilaHorariaEstado051, valor: string): void {
    onChange({ ...estado, [columna]: { ...estado[columna], [campo]: valor } });
  }

  const filaTexto = (campo: { clave: keyof FilaHorariaEstado051; i18n: string }): JSX.Element => (
    <tr key={campo.clave} className="border-t border-border">
      <td className="sticky left-0 z-10 bg-hce-surface whitespace-nowrap p-1 pr-2 font-medium text-hce-ink">{t(`formularios.f051.partograma.tablaHoraria.${campo.i18n}`)}</td>
      {COLUMNAS_HORARIAS_PARTOGRAMA.map((c) => (
        <td key={c} className="p-0.5">
          <Input
            className="h-7 w-20 text-xs"
            aria-label={`${t(`formularios.f051.partograma.tablaHoraria.${campo.i18n}`)} ${c}`}
            value={estado[c][campo.clave]}
            onChange={(e) => actualizarCelda(c, campo.clave, e.target.value)}
          />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-hce-ink">{t('formularios.f051.partograma.tablaHoraria.titulo')}</p>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="border-collapse text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-hce-surface p-1 text-left font-normal text-hce-muted">
                <span className="sr-only">{t('formularios.f051.partograma.tablaHoraria.titulo')}</span>
              </th>
              {COLUMNAS_HORARIAS_PARTOGRAMA.map((c) => (
                <th key={c} className="p-1 text-center font-medium text-hce-ink">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filaTexto(HORA_REAL)}
            <FilaSelect
              etiqueta={t('formularios.f051.partograma.tablaHoraria.posicionMaterna')}
              opciones={POSICIONES_MATERNAS_PARTOGRAMA}
              textoOpcion={(o) => `${o} — ${t(`formularios.f051.partograma.posicionMaternaOpciones.${o}`)}`}
              valor={(c) => estado[c].posicionMaterna}
              onChange={(c, v) => actualizarCelda(c, 'posicionMaterna', v)}
            />
            {CAMPOS_TEXTO.map(filaTexto)}
            <FilaSelect
              etiqueta={t('formularios.f051.partograma.tablaHoraria.dolorLocalizacion')}
              opciones={LOCALIZACIONES_DOLOR_PARTOGRAMA}
              textoOpcion={(o) => t(`formularios.f051.partograma.localizacionOpciones.${o}`)}
              valor={(c) => estado[c].dolorLocalizacion}
              onChange={(c, v) => actualizarCelda(c, 'dolorLocalizacion', v)}
            />
            <FilaSelect
              etiqueta={t('formularios.f051.partograma.tablaHoraria.dolorIntensidad')}
              opciones={INTENSIDADES_DOLOR_PARTOGRAMA}
              textoOpcion={(o) => t(`formularios.f051.partograma.intensidadOpciones.${o}`)}
              valor={(c) => estado[c].dolorIntensidad}
              onChange={(c, v) => actualizarCelda(c, 'dolorIntensidad', v)}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}
